import React from 'react';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import Bullets from 'slickpay/common/Bullets.component';

import Permitted from 'components/core/Permitted.component';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import PERM from 'constants/core/perm.const';
import { SPLIT_TAKINGS_IMAGES } from 'constants/images/hrManagement.const';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import { useCountryImage } from 'hooks/useCountryImage.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';

import CN from './StaffCommissions.module.scss';

const TC_LINK = 'https://www.getslick.com/legal/terms';
const LEARN_LINK = 'https://help.getslick.com/en/articles/10767655-split-commissions-faq-s';
const LINK_HELP = 'https://help.getslick.com/en/articles/11686307-split-takings-for-chair-renters';

const getBullets = (t) => [
  <div className={CN.bullet}>{t('humanResources.splitTakings.banner.bullet1')}</div>,
  <div className={CN.bullet}>{t('humanResources.splitTakings.banner.bullet2')}</div>,
  <div className={CN.bullet}>
    {t('humanResources.splitTakings.banner.bullet3Part1')}
    {S.space}
    <div className={CN.textBold}>{t('humanResources.splitTakings.banner.bullet3Part2')}</div>
    {t('humanResources.splitTakings.banner.bullet3Part3')}
  </div>,
  <div className={CN.bullet}>
    {t('humanResources.splitTakings.banner.bullet4Part1')}
    <ExternalLink link={TC_LINK} variant="inline">
      <span className={CN.extrLink}>{t('humanResources.splitTakings.banner.bullet4Part2')}</span>
    </ExternalLink>
    {t('humanResources.splitTakings.banner.bullet4Part3')}
  </div>,
];

const CommissionsToolbar = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const splitTakingsDiagram = useCountryImage(SPLIT_TAKINGS_IMAGES);
  const salonId = useCurrentSalonId();

  const reportsCommissions = linkTo({ pattern: LINK.reportsCommissions, params: { salonId }, fault: '#' });

  return (
    <div data-bem="CommissionsToolbar" className={CN.toolbar}>
      <div
        data-bem="CommissionsToolbar__toolbar"
        className={cn({
          [CN.floatingToolbar]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        <div data-bem="CommissionsToolbar__panel" className={CN.panel}>
          <div className={CN.flex}>
            <div className={CN.pageTitle}>
              <div className={CN.iconContainer}>
                <Icon variant="moneyPaper" width="xl" />
              </div>
              {t('humanResources.splitTakings.banner.title')}
            </div>

            <div className={CN.toolbarButtonsContainer} data-bem="CommissionsToolbar__panel-buttons">
              <div className={CN.learnMoreContainer}>
                <ExternalLink link={LEARN_LINK} variant="inline">
                  <div data-bem="KpiSummary__button" className={CN.inner}>
                    <span className={CN.learnMore}>{t('humanResources.splitTakings.banner.moreInfoButton')}</span>
                  </div>
                </ExternalLink>
              </div>
              <Permitted value={PERM.canEditStaffCommissions} tag="CommissionsToolbar__can-edit-commissions">
                <NavLink to={reportsCommissions}>
                  <Button variant="primaryForce" onClick={alwaysNoop}>
                    <span className={CN.navLinkColor}>{t('humanResources.splitTakings.banner.reportTakingsButton')}</span>
                  </Button>
                </NavLink>
              </Permitted>
            </div>
          </div>
          <div className={CN.toolbarFlex}>
            <div className={CN.toolbarBulletsContainer}>
              <Bullets data={getBullets(t)} empty />
            </div>
            <div className={CN.toolbarImgContainer}>
              <img src={splitTakingsDiagram} className={CN.toolbarImg} alt="automate your payroll diagram" />
            </div>
          </div>
        </div>
      </div>
      <AlertPanel tag="CommissionsToolbar__alertPanel" variant="alert">
        <div className={CN.alert}>
          <div className={CN.alertTitle}>{t('humanResources.splitTakings.alertPanel.title')}</div>
          <div className={CN.alertBulletsContainer}>
            <div className={CN.bullet}>{t('humanResources.splitTakings.alertPanel.bullet1')}</div>
            <div className={CN.bullet}>{t('humanResources.splitTakings.alertPanel.bullet2')}</div>
            <div className={CN.bullet}>
              {t('humanResources.splitTakings.alertPanel.bullet3Part1')}
              <ExternalLink link={LINK_HELP} variant="inline">
                <div className={CN.linkHelp}>{t('humanResources.splitTakings.alertPanel.bullet3Part2')}</div>
              </ExternalLink>
            </div>
          </div>
        </div>
      </AlertPanel>
    </div>
  );
};
export default CommissionsToolbar;
